import { OrderGroup } from '../../generated/graphql';

import {
  BULK_PLANT,
  eventConstants,
  RECURRING_ORDER,
  INVOICE,
  ROUTE_BUILDER,
} from './eventConstants';
import { triggerMixPanelEvent } from './mixpanel';

const {
  MAIN_APP,
  STANDALONE,
  OPEN_DASHBOARD,
  RENDERED_SHIFT_LIST,
  RENDERED_LOADING_ORDER_LIST,
  RENDERED_DELIVERY_ORDER_LIST,
  OPEN_OPTIMIZED_DELIVERY_ORDER_MODAL,
  RENDERED_RECURRING_ORDER_LIST,
} = eventConstants;

const {
  Bulk_Tank_Inventory_Details_Opened,
  Bulk_Tank_Start_Inventory_Updated,
  Bulk_Warehouse_Inventory_Details_Opened,
  Details_Modal_Opened_From_Bulk_Plant_Inventory_Details_Page,
} = BULK_PLANT;

const {
  Recurring_Rule_Created_For_Both_Delivery_And_Loading_Order,
  Recurring_Rule_Created_For_Delivery_Order_Only,
} = RECURRING_ORDER;

const {
  Invoice_Created,
  Invoice_Generated,
  Invoice_Credited,
  Invoice_Re_Billed,
  Invoice_Re_Billed_Generated,
  Invoice_Re_Billed_Created,
  Invoice_Synced,
} = INVOICE;
const { Route_Created_From_Route_Builder, Route_Dispatched_From_Route_Builder } = ROUTE_BUILDER;

export const mixPanelEvents = {
  // Event Name -> New Movement Order Created
  newMovementOrderCreated: (params: OrderGroup | null | undefined) => {
    triggerMixPanelEvent('New Asset Movement Order Created', {
      orderId: params?.movementOrder?.id,
      requestedTankSize: params?.movementOrder?.requestedTankSize,
      requestedTankType: params?.movementOrder?.requestedTankType,
    });
  },

  // Event Name -> Movement Order Completed
  movementOrderStatusUpdate: (status: string) => {
    if (status === 'completed') {
      triggerMixPanelEvent('Movement Order Completed');
    } else if (status === 'archived') {
      triggerMixPanelEvent('Movement Order Archived');
    }
  },

  // Event Name -> Movement Order Location Updated
  movementOrderLocationUpdated: () => {
    triggerMixPanelEvent('Movement Order Asset Location Updated');
  },

  // Event Name -> Movement Order Detail View Opened
  movementOrderDetailOpened: () => {
    triggerMixPanelEvent('Movement Order Detail View Opened');
  },

  /****************************************
   *
   * Customer Portal Events
   *
   ****************************************/
  // Event Name -> Movement Order Detail View Closed
  customerPortalExportDeliveryOrdersToCSV: () => {
    triggerMixPanelEvent('Exported Delivery Orders to CSV');
  },
  // Event Name -> Order Created from Customer Portal
  customerPortalOrderCreated: () => {
    triggerMixPanelEvent('Order Created from Customer Portal');
  },

  // Event Name -> Dashboard Page Viewed
  dashboardPageViewed: () => {
    triggerMixPanelEvent('Dashboard Page Viewed');
  },

  /****************************************
   *
   * Weekly Shift Planner Events
   *
   ****************************************/

  // Event Name -> Weekly Shift Planner View Opened
  wShiftPlannerViewOpened: () => {
    triggerMixPanelEvent('Weekly Shift Planner View Opened');
  },

  // Event Name -> Weekly Shift Planner Unassigned=>Assigned
  wShiftPlannerOrderAssigned: <TParams>(params: TParams) => {
    triggerMixPanelEvent('Weekly Shift Planner Order Unassigned To Assigned', params);
  },

  // Event Name -> Weekly Shift Planner Assigned To UnAssigned
  wShiftPlannerOrderUnassigned: <TParams>(params: TParams) => {
    triggerMixPanelEvent('Weekly Shift Planner Order Assigned To Unassigned', params);
  },

  /****************************************
   *
   * Customer Portal Events
   *
   ****************************************/

  customerPortalInvitationSent: () => {
    triggerMixPanelEvent('Sent Customer Portal Invitation');
  },

  /****************************************
   *
   * Driver Notes Events
   *
   ***************************************/

  driverNotesPageOpened: () => {
    triggerMixPanelEvent('Driver Notes Page Opened');
  },

  driverNotesAcknowledged: () => {
    triggerMixPanelEvent('Driver Notes Acknowledged');
  },

  /****************************************
   *
   * Fees Events
   *
   ****************************************/

  // Event Name -> Flat Fee Created
  flatFeeCreated: () => {
    triggerMixPanelEvent('Flat Fee Created');
  },

  // Event Name -> Range Fee Created
  rangeFeeCreated: () => {
    triggerMixPanelEvent('Range Fee Created');
  },

  // Event Name -> New Range Fee Based on Distance
  newRangeFeeBasedOnDistance: () => {
    triggerMixPanelEvent('New Range Fee Based on Distance');
  },

  // Event Name => New Range Fee Based on Quantity
  newRangeFeeBasedOnQuantity: () => {
    triggerMixPanelEvent('New Range Fee Based on Quantity');
  },

  // Event Name => New Range Fee Based on Time
  newRangeFeeBasedOnTime: () => {
    triggerMixPanelEvent('New Range Fee Based on Time');
  },

  //Events Name => Added Fee on customer Pricing
  addedFeeOnCustomerPricing: () => {
    triggerMixPanelEvent('Added Fee on Customer Pricing ');
  },

  //Event Name => Added Fee on Ship To Pricing
  addedFeeOnShipToPricing: () => {
    triggerMixPanelEvent('Added Fee on Ship To Pricing');
  },

  /****************************************
   *
   * Pricing Events
   *
   ****************************************/

  // Event Name -> Pricing Imported
  pricingImported: () => {
    triggerMixPanelEvent('Pricing - New Pricing Imported');
  },

  // Event Name -> Pricing Imported Failed
  importPricingError: () => {
    triggerMixPanelEvent('Pricing - New Pricing Import Failed');
  },

  // Event Name -> Manual Pricing Imported
  manualPricingUpdate: () => {
    triggerMixPanelEvent('Pricing - Manual Pricing Update');
  },

  // Event Name -> Quote dispatch
  dispatchPricingQuotes: () => {
    triggerMixPanelEvent('Pricing - Quote dispatched to POC ');
  },

  // Event Name -> Pricing Copied from previous records
  pricingCopiedFromPreviousRecords: () => {
    triggerMixPanelEvent('Pricing - Pricing Copied From Previous Records');
  },

  // Event Name -> New Pricing Created
  newPricingCreated: () => {
    triggerMixPanelEvent('Pricing - New Pricing Created');
  },

  // Event Name -> New Customer Pricing Created
  customerPricingCreated: () => {
    triggerMixPanelEvent('Pricing - Customer Pricing Created');
  },

  // Event Name -> Customer Pricing Updated
  customerPricingUpdated: () => {
    triggerMixPanelEvent('Pricing - Customer Pricing Updated');
  },

  // Event Name -> ShipTo Pricing Created
  shipToPricingCreated: () => {
    triggerMixPanelEvent('Pricing - ShipTo Pricing Created');
  },

  // Event Name -> ShipTo Pricing Updated
  shipToPricingUpdated: () => {
    triggerMixPanelEvent('Pricing - ShipTo Pricing Updated');
  },

  // Event Name -> New Market Created
  newMarketCreated: () => {
    triggerMixPanelEvent('Pricing - New Market Created');
  },

  // Event Name -> New Tax Created
  newTaxCreated: () => {
    triggerMixPanelEvent('Pricing - New Tax Created');
  },

  // Event Name -> New Margin Created
  newMarginCreated: () => {
    triggerMixPanelEvent('Pricing - New Margin Created');
  },

  // Event Name -> New RateType Created
  newRateTypeCreated: () => {
    triggerMixPanelEvent('Pricing - New RateType Created');
  },

  /****************************************
   *
   * Tank Monitor Events
   *
   ****************************************/

  // Event Name -> TM - Page Opened
  tankMonitorPageOpened: () => {
    triggerMixPanelEvent('TM - Page Opened');
  },

  // Event Name -> TM - Filter By Provider
  tankMonitorFilterByProvider: () => {
    triggerMixPanelEvent('TM - Filter By Provider');
  },

  // Event Name -> TM - Order Detail View Opened Using Order No
  tankMonitorOrderDetailViewOpened: () => {
    triggerMixPanelEvent('TM - Order Detail View Opened Using Order No');
  },

  // Event Name -> TM - Asset Configuration Update Modal Opened
  tankMonitorAssetConfigurationUpdateModalOpened: () => {
    triggerMixPanelEvent('TM - Asset Configuration Update Modal Opened');
  },

  // Event Name -> TM - Asset Configuration Updated
  tankMonitorAssetConfigurationUpdated: () => {
    triggerMixPanelEvent('TM - Asset Configuration Updated');
  },

  // Event Name -> TM - Link Asset Modal Opened
  tankMonitorLinkAssetModalOpened: () => {
    triggerMixPanelEvent('TM - Link Asset Modal Opened');
  },

  // Event Name -> TM - Link Asset Opened by Right Click
  tankMonitorLinkAssetOpenedRightClick: () => {
    triggerMixPanelEvent('TM - Asset Linked by Right Click');
  },

  // Event Name -> TM - Asset Linked from TM Page
  tankMonitorAssetLinked: () => {
    triggerMixPanelEvent('TM - Asset Linked from TM Page');
  },

  // Event Name -> TM - Asset Unlinked from TM Page
  tankMonitorAssetUnlinked: () => {
    triggerMixPanelEvent('TM - Asset Unlinked from TM Page');
  },

  // Event Name -> TM- Order Created
  tankMonitorOrderCreated: () => {
    triggerMixPanelEvent('TM- Order Created');
  },

  //Event Name -> TM- Edit Configuration Modal Opened
  tankMonitorEditConfigurationModalOpened: () => {
    triggerMixPanelEvent('TM- Edit Configuration Modal Opened');
  },

  //Event Name -> TM- Edit Configuration Value Changed
  tankMonitorEditConfigurationValueChanged: (key: string[], deviceId: string) => {
    triggerMixPanelEvent(`TM- Edit Configuration Value Changed of ${key} for Device ${deviceId}`);
  },

  /****************************************
   *
   * Self Customer Asset Events
   *
   ****************************************/

  // Event Name -> Asset_Management.New_Customer_Asset_Of_Category_Tank.Added
  customerAssetOfCategoryTankAdded: () => {
    triggerMixPanelEvent('Asset_Management.New_Customer_Asset_Of_Category_Tank.Added');
  },

  /****************************************
   *
   * Dashboard Events
   *
   ****************************************/

  // Event Name -> Standalone Dashboard Open
  openStandaloneDashboard: () => {
    triggerMixPanelEvent(`${STANDALONE} - ${OPEN_DASHBOARD}`);
  },

  openMainAppDashboard: () => {
    triggerMixPanelEvent(`${MAIN_APP} - ${OPEN_DASHBOARD}`);
  },

  /****************************************
   *
   * Optimized Delivery Order Modal Events
   *
   ****************************************/

  // Event Name -> Optimized Delivery Order Modal Open
  openOptimizedDeliveryOrderModal: <TParams>(params: TParams) => {
    triggerMixPanelEvent(`${STANDALONE} - ${OPEN_OPTIMIZED_DELIVERY_ORDER_MODAL}`, params);
  },

  /****************************************
   *
   * Optimized Loading, Delivery, Recurring ,Shift List Events
   *
   ****************************************/

  // Event Name -> Optimized Delivery Order Modal Open
  renderedOptimizedDeliveryOrderList: () => {
    triggerMixPanelEvent(`${STANDALONE} - ${RENDERED_DELIVERY_ORDER_LIST}`);
  },
  renderedOptimizedLoadingOrderList: () => {
    triggerMixPanelEvent(`${STANDALONE} - ${RENDERED_LOADING_ORDER_LIST}`);
  },
  renderedOptimizedRecurringOrderList: () => {
    triggerMixPanelEvent(`${STANDALONE} - ${RENDERED_RECURRING_ORDER_LIST}`);
  },
  renderedOptimizedShiftList: () => {
    triggerMixPanelEvent(`${STANDALONE} - ${RENDERED_SHIFT_LIST}`);
  },

  /****************************************
   *
   * order Events
   *
   ****************************************/
  bothLubeAndBulkOrderCreated: (
    packagedDeliveryProductCount: number,
    bulkDeliveryProductCount: number
  ) => {
    triggerMixPanelEvent('New Lube Order and Bulk Delivery order Created', {
      packagedDeliveryProductCount,
      bulkDeliveryProductCount,
    });
  },
  /****************************************
   *
   * Inventory Adjustment Events
   *
   ****************************************/
  updateInventoryAdjustment: () => {
    triggerMixPanelEvent('Inventory Adjustment Updated');
  },
  reasonForInventoryAdjustment: (reason: string) => {
    triggerMixPanelEvent('Reason for Inventory Adjustment', {
      reason,
    });
  },

  /****************************************
   *
   * Bulk Plant Events
   *
   ****************************************/
  bulkTankDetailsOpened: () => {
    triggerMixPanelEvent(Bulk_Tank_Inventory_Details_Opened);
  },
  bulkWarehouseDetailsOpened: () => {
    triggerMixPanelEvent(Bulk_Warehouse_Inventory_Details_Opened);
  },
  bulkTankInventoryUpdated: () => {
    triggerMixPanelEvent(Bulk_Tank_Start_Inventory_Updated);
  },
  orderDetailsModalOpenedFromBulkPlantInventoryDetailsPage: (orderType: string) => {
    triggerMixPanelEvent(Details_Modal_Opened_From_Bulk_Plant_Inventory_Details_Page, {
      orderType,
    });
  },

  /****************************************
   *
   * Recurring Order Events
   *
   ****************************************/
  recurringOrderCreatedForDeliveryOnly: () => {
    triggerMixPanelEvent(Recurring_Rule_Created_For_Delivery_Order_Only);
  },
  recurringOrderCreatedForBothDeliveryAndLoading: () => {
    triggerMixPanelEvent(Recurring_Rule_Created_For_Both_Delivery_And_Loading_Order);
  },

  /****************************************
   *
   * Invoice Events
   *
   ****************************************/
  invoiceCreated: () => {
    triggerMixPanelEvent(Invoice_Created);
  },
  invoiceGenerated: () => {
    triggerMixPanelEvent(Invoice_Generated);
  },
  invoiceCredited: () => {
    triggerMixPanelEvent(Invoice_Credited);
  },
  invoiceRebilled: () => {
    triggerMixPanelEvent(Invoice_Re_Billed);
  },
  invoiceRebilledGenerated: () => {
    triggerMixPanelEvent(Invoice_Re_Billed_Generated);
  },
  invoiceRebilledCreated: () => {
    triggerMixPanelEvent(Invoice_Re_Billed_Created);
  },
  invoiceSynced: () => {
    triggerMixPanelEvent(Invoice_Synced);
  },

  /****************************************
   *
   * Route Builder Events
   *
   ****************************************/

  routeCreatedFromRouteBuilder: () => {
    triggerMixPanelEvent(Route_Created_From_Route_Builder);
  },

  routeDispatchedFromRouteBuilder: () => {
    triggerMixPanelEvent(Route_Dispatched_From_Route_Builder);
  },

  /****************************************
   *
   * Usage Metrics Events
   *
   ****************************************/

  usageMetricsLinkOpened: () => {
    triggerMixPanelEvent('USAGE_METRICS_URL_OPENED');
  },
};
